/** Loading, error, and empty states, worded for a customer rather than a log. */

import { Link } from 'react-router-dom';
import type { ReactNode } from 'react';

import { ApiRequestError } from '../lib/api';
import { AlertIcon, ArrowRight, CheckIcon } from './ui';

/** True when the server said the thing does not exist, rather than that it failed. */
export function isMissing(error: unknown): boolean {
  return error instanceof ApiRequestError && error.status === 404;
}

/** A board-shaped placeholder, so the page does not jump when rows arrive. */
export function BoardSkeleton({ rows = 6 }: { readonly rows?: number }): ReactNode {
  return (
    <div className="card board" role="status" aria-label="Loading listings">
      {Array.from({ length: rows }, (_, index) => (
        <div className="skelrow" key={index}>
          <div className="skeleton" style={{ height: 14, width: 20 }} />
          <div>
            <div className="skeleton" style={{ height: 13, width: `${58 - index * 4}%` }} />
            <div className="skeleton" style={{ height: 10, width: '34%', marginTop: 8 }} />
          </div>
          <div className="skeleton" style={{ height: 12 }} />
          <div className="skeleton" style={{ height: 14, marginLeft: 'auto', width: '72%' }} />
        </div>
      ))}
    </div>
  );
}

export function Loading({ label = 'Loading…' }: { readonly label?: string }): ReactNode {
  return (
    <p className="muted small" role="status">
      {label}
    </p>
  );
}

export function errorMessage(error: unknown): string {
  if (error instanceof ApiRequestError) return error.message;
  if (error instanceof Error && error.message !== '') return error.message;
  return 'That request failed.';
}

export function ErrorNotice({
  error,
  onRetry,
}: {
  readonly error: unknown;
  readonly onRetry?: () => void;
}): ReactNode {
  return (
    <div className="notice is-error" role="alert">
      <span className="notice-icon">
        <AlertIcon colour="#b3261e" />
      </span>
      <span>
        {errorMessage(error)}
        {onRetry === undefined ? null : (
          <>
            {' '}
            <button type="button" className="linkbtn" onClick={onRetry}>
              Try again
            </button>
          </>
        )}
      </span>
    </div>
  );
}

export function SuccessNotice({ children }: { readonly children: ReactNode }): ReactNode {
  return (
    <div className="notice is-positive">
      <span className="notice-icon">
        <CheckIcon />
      </span>
      <span>{children}</span>
    </div>
  );
}

export function CautionNotice({ children }: { readonly children: ReactNode }): ReactNode {
  return (
    <div className="notice is-caution">
      <span className="notice-icon">
        <AlertIcon />
      </span>
      <span>{children}</span>
    </div>
  );
}

export function EmptyState({
  headline,
  children,
  action,
}: {
  readonly headline?: string;
  readonly children: ReactNode;
  readonly action?: ReactNode;
}): ReactNode {
  return (
    <div className="card emptystate">
      {headline === undefined ? null : <div className="headline">{headline}</div>}
      <p>{children}</p>
      {action}
    </div>
  );
}

/**
 * A listing or company that is gone. Retrying a 404 only repeats it, so this
 * state offers the way onward the visitor actually wants instead of a button
 * that cannot work.
 */
export function MissingState({
  what,
  children,
}: {
  readonly what: string;
  readonly children?: ReactNode;
}): ReactNode {
  return (
    <div className="card emptystate">
      <div className="headline">We could not find that {what}</div>
      <p>
        {children ?? `It may have been filled or removed.`}
      </p>
      <Link className="pill pill-primary" to="/">
        Browse the leaderboard
        <ArrowRight />
      </Link>
    </div>
  );
}
